/**
 * IntentRouter.js
 * High-speed Deterministic Intent Router + Structured Multi-Action Planner.
 * 
 * Flow:
 * 1. Normalize speech (strip wake words and fillers)
 * 2. Check for sensitive credential inquiries -> Instant Refusal
 * 3. Check for deep agricultural requests -> Instant Local Voice Assistant Handoff
 * 4. Deterministic Multi-Action / Atomic Action Matcher (<5ms)
 * 5. Structured Groq AI Fallback (strictly returns action schema, never arbitrary JS)
 */

import { resolveRoute } from "./PageRouteRegistry.js";
import axios from "axios";
import { getBackendBaseUrl } from "../utils/apiUrl.js";

const API_BASE = getBackendBaseUrl();

export const stripWakeWords = (rawText) => {
  if (!rawText) return "";
  let clean = rawText.trim().replace(/^[.,!?;:'"/\\]+/, "");

  const wakeWordPrefixes = [
    /^(?:hey|hi|hello)\s+(?:kisan|kishan|kissan|kiss\s*an|kisaan|krishna|mithr|mitra)[,.]?\s*/i,
    /^kisan\s+(?:mithr|mitra)[,.]?\s*/i,
    /^kisan[,.]?\s*/i,
    /^(?:హే|నమస్తే)\s*(?:కిసాన్|కిషన్|కిసాన్\s*మిత్ర)[,.]?\s*/i,
    /^కిసాన్\s*మిత్ర[,.]?\s*/i,
    /^కిసాన్[,.]?\s*/i,
    /^(?:हे|नमस्ते)\s*(?:किसान|किसान\s*मित्र)[,.]?\s*/i,
    /^किसान\s*मित्र[,.]?\s*/i,
    /^किसान[,.]?\s*/i
  ];

  for (const regex of wakeWordPrefixes) {
    clean = clean.replace(regex, "");
  }

  const fillers = [
    /^(?:please\s+|can\s+you\s+(?:please\s+)?|could\s+you\s+(?:please\s+)?|i\s+want\s+to\s+(?:see\s+|check\s+)?|take\s+me\s+to\s+|navigate\s+to\s+|show\s+me\s+|open\s+up\s+)/i,
    /^(?:దయచేసి\s+|నాకు\s+|చూపించు\s+)/i,
    /^(?:कृपया\s+|मुझे\s+|दिखाइए\s+)/i
  ];

  for (const filler of fillers) {
    clean = clean.replace(filler, "");
  }

  return clean.trim();
};

export const normalizeCommand = (rawText) => {
  if (!rawText) return "";
  let clean = stripWakeWords(rawText).toLowerCase().replace(/^[.,!?;:'"/\\]+|[.,!?;:'"/\\]+$/g, "");

  // Normalize inverted commands: "[page] open" -> "open [page]" (e.g. "weather open" -> "open weather")
  clean = clean.replace(/\b([a-z0-9\s_-]+?)\s+(?:open|kholo|teravandi|chupinchu|dikhao)$/i, "open $1");
  // Normalize prefix verbs: "go [page]" -> "open [page]" (e.g. "go weather" -> "open weather", "go my field" -> "open my field")
  clean = clean.replace(/^go\s+(?!back\b|forward\b|to\s+top\b|to\s+bottom\b|top\b|bottom\b)(?:to\s+)?/i, "open ");
  clean = clean.replace(/^(?:show\s+me|show|take\s+me\s+to|bring\s+up|load|view|navigate\s+to|navigate)\s+/i, "open ");

  return clean.trim();
};

/**
 * Checks if a string contains the official wake phrase "Hey Kisan"
 */
export const hasWakeWord = (transcript) => {
  if (!transcript) return false;
  const lower = transcript.toLowerCase();
  const wakeRegex = /\b(hey\s+kisan|hi\s+kisan|hello\s+kisan|hey\s+kishan|hey\s+kiss\s+an|hey\s+kissan|kisan\s+mithr|హే\s*కిసాన్|కిసాన్\s*మిత్ర|हे\s*किसान|किसान\s*मित्र)\b/i;
  return wakeRegex.test(lower);
};

export const isWakeWordOnly = (transcript) => {
  if (!transcript) return false;
  const clean = transcript.toLowerCase().trim().replace(/^[.,!?;:'"/\\]+|[.,!?;:'"/\\]+$/g, "");
  const wakeGreetingRegex = /^(hey\s*ki|hey\s*kisan|hi\s*kisan|hello\s*kisan|hey\s*kishan|hey\s*kissan|kisan\s*mithr|kisan\s*mitra|kisan|హే\s*కిసాన్|కిసాన్\s*మిత్ర|నమస్తే\s*కిసాన్|కిసాన్|हे\s*किसान|किसान\s*मित्र|नमस्ते\s*किसान|किसान)$/i;
  return wakeGreetingRegex.test(clean);
};

export const extractCommandFromWakeWord = (transcript) => {
  if (!transcript) return "";
  return normalizeCommand(transcript);
};

/**
 * Checks for sensitive security requests that should NEVER be filled by voice.
 */
export const checkSensitiveCommand = (text) => {
  const norm = text.toLowerCase();
  const sensitivePatterns = [
    /\b(password|passcode|pwd)\b/i,
    /\b(otp|one time password)\b/i,
    /\b(pin|security pin)\b/i,
    /\b(card number|credit card|debit card|cvv)\b/i,
    /\b(auth token|secret key|api key)\b/i
  ];

  for (const pattern of sensitivePatterns) {
    if (pattern.test(norm)) {
      if (norm.includes("otp")) {
        return {
          isSensitive: true,
          spokenFeedback: "For your security, please enter the OTP yourself."
        };
      }
      if (norm.includes("card") || norm.includes("cvv")) {
        return {
          isSensitive: true,
          spokenFeedback: "For your security, please enter payment details yourself."
        };
      }
      return {
        isSensitive: true,
        spokenFeedback: "For your security, please enter the password yourself."
      };
    }
  }

  return { isSensitive: false };
};

/**
 * Detects if a request is a long/complex agricultural inquiry that belongs in the Local Voice Assistant.
 */
export const checkLongAgriculturalRequest = (text) => {
  const norm = text.toLowerCase();

  const detailedMarkers = [
    /\bin detail\b/i,
    /\bdetail(?:ed)?\b/i,
    /\bstep by step\b/i,
    /\bcomplete (?:farming )?plan\b/i,
    /\btell me everything about\b/i,
    /\bexplain (?:how|why|what|in detail)\b/i,
    /\bfrom sowing to harvesting\b/i,
    /\bdosage and timing\b/i,
    /\bmedicine(?:s)? (?:and|with) dosage\b/i,
    /\bవివరంగా చెప్పండి\b/i,
    /\bపూర్తి ప్రణాళిక\b/i,
    /\bవిస్తారంగా\b/i,
    /\bविस्तार से बताओ\b/i,
    /\bपूरी योजना\b/i,
    /\bशुरुआत से कटाई तक\b/i
  ];

  // Specific check for long farming prompts
  const isDetailedQuery = detailedMarkers.some((marker) => marker.test(norm));
  const isWordyAgriQuestion = (norm.split(/\s+/).length >= 9) &&
    (norm.includes("pest") || norm.includes("disease") || norm.includes("cotton") || norm.includes("rice") || norm.includes("soil") || norm.includes("fertilizer"));

  if (isDetailedQuery || isWordyAgriQuestion) {
    return {
      isLongRequest: true,
      spokenFeedback: "That's a detailed request. Please use the AI Voice Assistant."
    };
  }

  return { isLongRequest: false };
};

/**
 * Parses single atomic commands into structured action objects.
 */
export const parseAtomicCommand = (commandText) => {
  const norm = normalizeCommand(commandText);
  if (!norm) return null;

  // 1. Current Page / Location Query
  if (/^(where am i|what page am i on|what is this page|what page is this|ఎక్కడ ఉన్నాను|मैं कहां हूँ)\b/i.test(norm)) {
    return {
      intent: "QUERY_PAGE_INFO",
      actions: [{ type: "query_page_info" }],
      spokenFeedback: null,
      confidence: 0.99
    };
  }

  // 2. Cancellation / Stop
  if (/^(cancel|stop|never mind|వద్దు|ఆపు|रद्द करो|रुको)$/i.test(norm)) {
    return {
      intent: "CANCEL_ACTION",
      actions: [{ type: "cancel" }],
      spokenFeedback: "Cancelled.",
      confidence: 0.99
    };
  }

  // 3. History Navigation
  if (/^(go back|previous page|back|వెనక్కి|పీచే जाओ|వాపస్ जाओ|पीछे जाओ|वापस जाओ)$/i.test(norm)) {
    return {
      intent: "GO_BACK",
      actions: [{ type: "go_back" }],
      spokenFeedback: "Going back.",
      confidence: 0.99
    };
  }
  if (/^(go forward|forward|next page|ముందుకు|आगे जाओ)$/i.test(norm)) {
    return {
      intent: "GO_FORWARD",
      actions: [{ type: "go_forward" }],
      spokenFeedback: "Going forward.",
      confidence: 0.99
    };
  }
  if (/^(refresh page|reload page|refresh|reload|రీఫ్రెష్|रिफ्रेश)$/i.test(norm)) {
    return {
      intent: "RELOAD",
      actions: [{ type: "reload" }],
      spokenFeedback: "Refreshing page.",
      confidence: 0.99
    };
  }

  // 3b. Open & Close Menu / Sidebar
  if (/^(?:open|show)\s+(?:the\s+)?(?:mobile\s+|side\s+|sidebar\s+)?menu$/i.test(norm) || /మెనూ తెరవండి|మెనూ ఓపెన్|मेनू खोलो/.test(norm)) {
    return {
      intent: "OPEN_MENU",
      actions: [{ type: "open_menu" }],
      spokenFeedback: "Opening the menu.",
      confidence: 0.98
    };
  }
  if (/^(?:close|hide)\s+(?:the\s+)?(?:mobile\s+|side\s+|sidebar\s+)?menu$/i.test(norm) || /మెనూ మూసివేయండి|మెనూ క్లోజ్|मेनू बंद करो/.test(norm)) {
    return {
      intent: "CLOSE_MENU",
      actions: [{ type: "close_menu" }],
      spokenFeedback: "Closing the menu.",
      confidence: 0.98
    };
  }

  // 3c. Switch App Language / Select Language
  const langMatch = norm.match(/^(?:switch|change|set|select|choose)\s+(?:to\s+)?(?:the\s+)?(?:app\s+)?(?:language\s+to\s+|language\s+)?(telugu|hindi|english)(?:\s+language)?$/i) ||
                    norm.match(/^(?:switch|change|set|select|choose)\s+to\s+(telugu|hindi|english)$/i) ||
                    norm.match(/^(telugu|hindi|english)\s+language$/i);
  if (langMatch && langMatch[1]) {
    const rawL = langMatch[1].toLowerCase();
    const code = rawL === "telugu" ? "te" : (rawL === "hindi" ? "hi" : "en");
    const langName = code === "te" ? "Telugu" : (code === "hi" ? "Hindi" : "English");
    return {
      intent: "SWITCH_LANGUAGE",
      actions: [{ type: "switch_language", language: code, languageName: langName }],
      spokenFeedback: `Selected ${langName}.`,
      confidence: 0.98
    };
  }
  if (/తెలుగులోకి\s+మార్చు|తెలుగు\s+భాష|తెలుగు\s+ఎంచుకో/.test(norm)) {
    return {
      intent: "SWITCH_LANGUAGE",
      actions: [{ type: "switch_language", language: "te", languageName: "Telugu" }],
      spokenFeedback: "భాష తెలుగులోకి మార్చబడింది.",
      confidence: 0.98
    };
  }
  if (/हिंदी\s+में\s+बदलो|हिंदी\s+भाषा|हिंदी\s+चुनो/.test(norm)) {
    return {
      intent: "SWITCH_LANGUAGE",
      actions: [{ type: "switch_language", language: "hi", languageName: "Hindi" }],
      spokenFeedback: "भाषा हिंदी में बदल दी गई है।",
      confidence: 0.98
    };
  }

  // 3d. Tab Switching
  if (/^(?:switch|go)\s+to\s+(?:the\s+)?(?:next\s+tab|tab)$/i.test(norm) || norm === "next tab" || norm === "switch tab" || norm === "switch to the next tab") {
    return {
      intent: "SWITCH_TAB",
      actions: [{ type: "switch_tab", target: "next" }],
      spokenFeedback: "Switched to next tab.",
      confidence: 0.96
    };
  }
  const tabMatch = norm.match(/^(?:switch|go|open)\s+to\s+(?:the\s+)?([a-zA-Z\s]+?)\s+tab$/i) ||
                   norm.match(/^open\s+([a-zA-Z\s]+?)\s+tab$/i);
  if (tabMatch && tabMatch[1]) {
    const tabTarget = tabMatch[1].trim();
    return {
      intent: "SWITCH_TAB",
      actions: [{ type: "switch_tab", target: tabTarget }],
      spokenFeedback: `Switched to ${tabTarget} tab.`,
      confidence: 0.95
    };
  }

  // 3e. Toggles / Switches (Turn on / Turn off / Enable / Disable)
  const toggleMatch = norm.match(/^(?:turn|switch)\s+(?:the\s+)?([a-zA-Z\s]+?)\s+(on|off)$/i) ||
                      norm.match(/^(?:turn|switch)\s+(on|off)\s+(?:the\s+)?([a-zA-Z\s]+)$/i) ||
                      norm.match(/^(enable|disable)\s+(?:the\s+)?([a-zA-Z\s]+)$/i);
  if (toggleMatch) {
    let target = "this";
    let state = true;
    if (toggleMatch[1] === "on" || toggleMatch[1] === "off") {
      state = toggleMatch[1] === "on";
      target = toggleMatch[2]?.trim() || "this";
    } else if (toggleMatch[1] === "enable" || toggleMatch[1] === "disable") {
      state = toggleMatch[1] === "enable";
      target = toggleMatch[2]?.trim() || "this";
    } else {
      target = toggleMatch[1]?.trim() || "this";
      state = toggleMatch[2] === "on";
    }
    return {
      intent: "TOGGLE_SETTING",
      actions: [{ type: "toggle", target, state }],
      spokenFeedback: state ? "Turned on." : "Turned off.",
      confidence: 0.95
    };
  }

  // 4. Scrolling Controls
  // Top / Bottom
  if (/^(?:scroll\s+to|go\s+to|move\s+to|show\s+me\s+)?\s*(?:the\s+)?top$/i.test(norm) || /పైకి వెళ్లు|పైకి వెళ్ళు|శీర్షిక|शीर्ष पर जाओ/.test(norm)) {
    return {
      intent: "SCROLL_TOP",
      actions: [{ type: "scroll_top" }],
      spokenFeedback: "Moved to the top.",
      confidence: 0.98
    };
  }
  if (/^(?:scroll\s+to|go\s+to|move\s+to|show\s+me\s+)?\s*(?:the\s+)?bottom$/i.test(norm) || /అడుగున|అడుగుకి వెళ్లు|नीचे तक/.test(norm)) {
    return {
      intent: "SCROLL_BOTTOM",
      actions: [{ type: "scroll_bottom" }],
      spokenFeedback: "Moved to the bottom.",
      confidence: 0.98
    };
  }

  // Scroll Down variations (small, medium, large)
  if (/(?:scroll|move|go)\s+(?:down\s+more|more\s+down|down\s+a\s+lot)/i.test(norm)) {
    return {
      intent: "SCROLL_DOWN",
      actions: [{ type: "scroll", direction: "down", amount: "large" }],
      spokenFeedback: "Scrolling down.",
      confidence: 0.98
    };
  }
  if (/(?:scroll|move|go)\s+(?:down\s+a\s+little|a\s+little\s+down|down\s+slightly|slightly\s+down|a\s+bit\s+down)/i.test(norm) || norm === "move down a little") {
    return {
      intent: "SCROLL_DOWN",
      actions: [{ type: "scroll", direction: "down", amount: "small" }],
      spokenFeedback: "Done.",
      confidence: 0.98
    };
  }
  if (/(?:scroll|move|go)\s+down\b/i.test(norm) || norm === "scroll down" || /కిందికి స్క్రోల్|నీచే/.test(norm)) {
    return {
      intent: "SCROLL_DOWN",
      actions: [{ type: "scroll", direction: "down", amount: "medium" }],
      spokenFeedback: "Scrolling down.",
      confidence: 0.96
    };
  }

  // Scroll Up variations (small, medium, large)
  if (/(?:scroll|move|go)\s+(?:up\s+more|more\s+up|up\s+a\s+lot)/i.test(norm)) {
    return {
      intent: "SCROLL_UP",
      actions: [{ type: "scroll", direction: "up", amount: "large" }],
      spokenFeedback: "Scrolling up.",
      confidence: 0.98
    };
  }
  if (/(?:scroll|move|go)\s+(?:up\s+a\s+little|a\s+little\s+up|up\s+slightly|slightly\s+up|a\s+bit\s+up)/i.test(norm) || norm === "move up a little") {
    return {
      intent: "SCROLL_UP",
      actions: [{ type: "scroll", direction: "up", amount: "small" }],
      spokenFeedback: "Done.",
      confidence: 0.98
    };
  }
  if (/(?:scroll|move|go)\s+up\b/i.test(norm) || norm === "scroll up" || /పైకి స్క్రోల్|ऊपर/.test(norm)) {
    return {
      intent: "SCROLL_UP",
      actions: [{ type: "scroll", direction: "up", amount: "medium" }],
      spokenFeedback: "Scrolling up.",
      confidence: 0.96
    };
  }

  // 5. Clear Input Field
  const clearMatch = norm.match(/(?:clear|remove\s+the\s+text\s+from|empty)\s+(?:the\s+)?([a-zA-Z\s]+?)(?:\s+(?:field|box|input))?$/i) ||
                     norm === "clear" || norm === "clear field" || norm === "clear input";
  if (clearMatch) {
    const target = typeof clearMatch === "object" && clearMatch[1] ? clearMatch[1].trim() : "active";
    return {
      intent: "CLEAR_INPUT",
      actions: [{ type: "clear_input", target }],
      spokenFeedback: "Cleared.",
      confidence: 0.96
    };
  }

  // 6. Focus / Select Input Field
  const focusMatch = norm.match(/(?:focus\s+(?:on\s+)?|select\s+the\s+|choose\s+the\s+)([a-zA-Z\s]+?)(?:\s+(?:field|box|input))$/i) ||
                     norm.match(/^select\s+(?:the\s+)?([a-zA-Z\s]+?)\s+field$/i);
  if (focusMatch && focusMatch[1]) {
    const target = focusMatch[1].trim();
    return {
      intent: "FOCUS_INPUT",
      actions: [{ type: "focus_input", target }],
      spokenFeedback: "Done.",
      confidence: 0.95
    };
  }

  // 7. Compound pattern: "select the name field and enter Vignesh"
  const rawClean = stripWakeWords(commandText);
  const selectAndEnterMatch = rawClean.match(/(?:select|focus(?:\s+on)?)\s+(?:the\s+)?([a-zA-Z\s]+?)(?:\s+(?:field|box|input))?\s+(?:and\s+)?(?:enter|type|put|write)\s+(.+)/i);
  if (selectAndEnterMatch && selectAndEnterMatch[1] && selectAndEnterMatch[2]) {
    const target = selectAndEnterMatch[1].trim().toLowerCase();
    const value = selectAndEnterMatch[2].trim();
    return {
      intent: "SELECT_AND_FILL",
      actions: [
        { type: "focus_input", target },
        { type: "fill_input", target, value }
      ],
      spokenFeedback: "Done.",
      confidence: 0.98
    };
  }

  // 8. Fill Input Field
  const fillPatterns = [
    /^(?:enter|type|put|write|fill)\s+(.+?)\s+(?:in|into|for)\s+(?:the\s+)?([a-zA-Z\s]+?)(?:\s+(?:field|box|input))?$/i,
    /^(?:in|into)\s+(?:the\s+)?([a-zA-Z\s]+?)(?:\s+(?:field|box|input))?\s+(?:enter|type|put)\s+(.+?)$/i
  ];

  // Search pattern shorthand ("Search for rice")
  const searchMatch = rawClean.match(/^search\s+(?:for\s+)?(.+)$/i);
  if (searchMatch && searchMatch[1]) {
    return {
      intent: "SEARCH_QUERY",
      actions: [
        { type: "fill_input", target: "search", value: searchMatch[1].trim() },
        { type: "click", target: "search" }
      ],
      spokenFeedback: "Searching.",
      confidence: 0.96
    };
  }

  // Pattern 1: Enter [value] in [target] field
  const match1 = rawClean.match(fillPatterns[0]);
  if (match1 && match1[1] && match1[2]) {
    const value = match1[1].trim();
    const target = match1[2].trim().toLowerCase();
    return {
      intent: "FILL_INPUT",
      actions: [{ type: "fill_input", target, value }],
      spokenFeedback: "Done.",
      confidence: 0.96
    };
  }

  // Pattern 2: In [target] enter [value]
  const match2 = rawClean.match(fillPatterns[1]);
  if (match2 && match2[1] && match2[2]) {
    const target = match2[1].trim().toLowerCase();
    const value = match2[2].trim();
    return {
      intent: "FILL_INPUT",
      actions: [{ type: "fill_input", target, value }],
      spokenFeedback: "Done.",
      confidence: 0.96
    };
  }

  // Pattern 3: Standalone "enter [value]" (e.g. "enter Vignesh" after selecting field)
  const match3 = rawClean.match(/^(?:enter|type|put|write)\s+([a-zA-Z0-9@._\s]+)$/i);
  if (match3 && match3[1] && !norm.includes("field") && !norm.includes("weather") && !norm.includes("contact")) {
    const value = match3[1].trim();
    return {
      intent: "FILL_INPUT",
      actions: [{ type: "fill_input", target: "active", value }],
      spokenFeedback: "Entered.",
      confidence: 0.93
    };
  }

  // 9. Button Clicks & Form Submissions
  if (/^(?:click\s+submit|submit\s+the\s+form|submit\s+this\s+form|submit\s+form|submit|send\s+the\s+message|send\s+message|సబ్మిట్|సమర్పించు|जमा करें)$/i.test(norm)) {
    return {
      intent: "SUBMIT_FORM",
      actions: [{ type: "submit", target: "submit" }],
      spokenFeedback: "Done.",
      confidence: 0.97
    };
  }
  if (/^(?:press\s+enter|hit\s+enter|press\s+return|enter\s+key)$/i.test(norm)) {
    return {
      intent: "PRESS_ENTER",
      actions: [{ type: "enter" }],
      spokenFeedback: "Done.",
      confidence: 0.98
    };
  }
  if (/^(?:press\s+search|click\s+search|శోధించు|खोजें)$/i.test(norm) || norm === "search") {
    return {
      intent: "CLICK_BUTTON",
      actions: [{ type: "click", target: "search" }],
      spokenFeedback: "Searching.",
      confidence: 0.96
    };
  }
  const clickMatch = norm.match(/^(?:click|press|tap)\s+(?:the\s+)?([a-zA-Z\s]+?)(?:\s+(?:button|link))?$/i);
  if (clickMatch && clickMatch[1]) {
    const target = clickMatch[1].trim();
    return {
      intent: "CLICK_BUTTON",
      actions: [{ type: "click", target }],
      spokenFeedback: "Done.",
      confidence: 0.93
    };
  }

  // 10. Dropdown Select (e.g. "Select Telangana", "Choose Hyderabad", "Select cotton")
  const selectMatch = norm.match(/^(?:select|choose)\s+(.+?)(?:\s+in\s+([a-zA-Z\s]+))?$/i);
  if (selectMatch && selectMatch[1]) {
    const optionVal = selectMatch[1].trim();
    const dropdownTarget = selectMatch[2] ? selectMatch[2].trim() : optionVal;
    return {
      intent: "SELECT_OPTION",
      actions: [{ type: "select_option", target: dropdownTarget, value: optionVal }],
      spokenFeedback: "Selected.",
      confidence: 0.95
    };
  }

  // 11. Direct Page Navigation
  // Strip common navigation verbs: "open", "go to", "show", "take me to", "view"
  const navClean = norm.replace(/^(?:go\s+to|open|show|take\s+me\s+to|view)\s+/i, "").trim();
  const matchedRoute = resolveRoute(navClean) || resolveRoute(norm);

  if (matchedRoute) {
    return {
      intent: `OPEN_${matchedRoute.id}`,
      actions: [{ type: "navigate", target: matchedRoute.path }],
      spokenFeedback: `Opening ${matchedRoute.name}.`,
      confidence: 0.98
    };
  }

  return null;
};

/**
 * Multi-action parser: Handles compound sentences joined by "and", "then", etc.
 * Example: "Open contact and enter Vignesh in the name field"
 * Example: "Open weather and scroll down a little"
 */
export const matchDeterministicIntent = (commandText) => {
  if (!commandText || typeof commandText !== "string") {
    return { intent: null, confidence: 0, actions: [] };
  }

  // 0. Standalone Wake Word Greeting ("Hey Kisan", "హే కిసాన్", "हे किसान")
  if (isWakeWordOnly(commandText)) {
    const lower = commandText.toLowerCase();
    const isTelugu = /[\u0C00-\u0C7F]/.test(lower) || lower.includes("కిసాన్");
    const isHindi = /[\u0900-\u097F]/.test(lower) || lower.includes("किसान");
    let reply = "Hello! I'm listening. How can I help with your farming today?";
    if (isTelugu) {
      reply = "నమస్కారం! నేను వింటున్నాను. మీకు ఎలా సహాయపడగలను?";
    } else if (isHindi) {
      reply = "नमस्ते! मैं सुन रहा हूँ। बताइए आपकी क्या सहायता करूँ?";
    }
    return {
      intent: "WAKE_WORD_GREETING",
      actionType: "greeting",
      confidence: 0.99,
      spokenFeedback: reply,
      actions: [{ type: "greeting" }],
      source: "wake_greeting"
    };
  }

  const norm = normalizeCommand(commandText);
  if (!norm) {
    return { intent: null, confidence: 0, actions: [] };
  }

  // 1. Security Check
  const sensitiveCheck = checkSensitiveCommand(norm);
  if (sensitiveCheck.isSensitive) {
    return {
      intent: "SENSITIVE_REFUSAL",
      confidence: 1.0,
      spokenFeedback: sensitiveCheck.spokenFeedback,
      actions: [],
      source: "security_guard"
    };
  }

  // 2. Long Agricultural Request Check
  const longReqCheck = checkLongAgriculturalRequest(norm);
  if (longReqCheck.isLongRequest) {
    return {
      intent: "LOCAL_ASSISTANT_HANDOFF",
      confidence: 0.99,
      spokenFeedback: longReqCheck.spokenFeedback,
      actions: [{ type: "open_local_voice_assistant" }],
      source: "handoff_classifier"
    };
  }

  const rawClean = stripWakeWords(commandText);

  // 3. Fast check for compound select-and-fill: "select the name field and enter Vignesh"
  const selectAndEnterMatch = rawClean.match(/^(?:select|focus(?:\s+on)?)\s+(?:the\s+)?([a-zA-Z\s]+?)(?:\s+(?:field|box|input))?\s+(?:and\s+)?(?:enter|type|put|write)\s+(.+)$/i);
  if (selectAndEnterMatch && selectAndEnterMatch[1] && selectAndEnterMatch[2]) {
    const target = selectAndEnterMatch[1].trim().toLowerCase();
    const value = selectAndEnterMatch[2].trim();
    return {
      intent: "FILL_INPUT",
      confidence: 0.98,
      actions: [
        { type: "focus_input", target },
        { type: "fill_input", target, value }
      ],
      spokenFeedback: "Done.",
      source: "deterministic_select_fill"
    };
  }

  // 4. Try compound command splitting by comma, "and", "then", etc.
  const conjunctionRegex = /(?:,\s*(?:and\s+then|and|then)?|\s+(?:and\s+then|and|then|మరియు|తర్వాత|और|फिर))\s+/i;
  if (conjunctionRegex.test(rawClean)) {
    const parts = rawClean.split(conjunctionRegex).map((p) => p.trim()).filter(Boolean);
    if (parts.length > 1) {
      const allActions = [];
      let allConfidence = 0.95;
      let lastFocusedTarget = null;

      for (const part of parts) {
        const subResult = parseAtomicCommand(part);
        if (subResult && subResult.actions && subResult.actions.length > 0) {
          for (const act of subResult.actions) {
            if (act.type === "focus_input" && act.target) {
              lastFocusedTarget = act.target;
            }
            if (act.type === "fill_input" && act.target === "active" && lastFocusedTarget) {
              act.target = lastFocusedTarget;
            }
            allActions.push(act);
          }
        } else {
          // If any sub-part failed deterministic parsing, abandon compound shortcut and fall through
          allActions.length = 0;
          break;
        }
      }

      if (allActions.length > 0) {
        // Build combined spoken feedback
        let feedback = "Done.";
        const hasNav = allActions.some((a) => a.type === "navigate");
        const hasFill = allActions.some((a) => a.type === "fill_input");
        const hasScroll = allActions.some((a) => a.type === "scroll");

        if (hasNav && hasScroll) {
          const navAct = allActions.find((a) => a.type === "navigate");
          const page = resolveRoute(navAct?.target);
          feedback = `Opening ${page ? page.name : "page"} and scrolling down.`;
        } else if (hasNav && hasFill) {
          const fillAct = allActions.find((a) => a.type === "fill_input");
          feedback = fillAct?.target?.includes("email")
            ? "Done. I've entered the email."
            : "Done. I've entered the details.";
        } else {
          feedback = "Done.";
        }

        return {
          intent: "MULTI_ACTION",
          confidence: allConfidence,
          actions: allActions,
          spokenFeedback: feedback,
          source: "deterministic_compound"
        };
      }
    }
  }

  // 4. Try single atomic command
  const atomic = parseAtomicCommand(commandText);
  if (atomic) {
    return {
      ...atomic,
      source: "deterministic_atomic"
    };
  }

  return {
    intent: "UNKNOWN",
    confidence: 0.2,
    actions: []
  };
};

/**
 * Main command router:
 * Fast deterministic match first (<5ms). Only queries backend AI if ambiguous.
 */
export const routeVoiceCommand = async (commandText, options = {}) => {
  const { allowLlmFallback = true, userName = "Farmer" } = options;
  const deterministicResult = matchDeterministicIntent(commandText);

  // High confidence deterministic result -> execute immediately!
  if (deterministicResult.confidence >= 0.85 && deterministicResult.intent !== "UNKNOWN") {
    return deterministicResult;
  }

  // If ambiguous or natural phrasing needs AI reasoning
  if (allowLlmFallback) {
    try {
      const response = await axios.post(
        `${API_BASE}/api/voice/ollama-intent`,
        {
          command: commandText,
          candidateIntent: deterministicResult.intent,
          candidateConfidence: deterministicResult.confidence,
          userName
        },
        { timeout: 4000 }
      );

      if (response.data) {
        const data = response.data;
        // Validate structured actions
        if (Array.isArray(data.actions) && data.actions.length > 0) {
          return {
            intent: data.intent || "AI_STRUCTURED_ACTION",
            confidence: data.confidence || 0.90,
            actions: data.actions,
            spokenFeedback: data.spokenFeedback || "Done.",
            source: data.source || "groq_ai"
          };
        }

        // Check if backend resolved a known intent
        if (data.intent && data.intent !== "UNKNOWN") {
          const mapped = parseAtomicCommand(data.intent.replace("OPEN_", "").toLowerCase());
          if (mapped) {
            return {
              ...mapped,
              spokenFeedback: data.spokenFeedback || mapped.spokenFeedback,
              source: "groq_intent_mapped"
            };
          }
        }
      }
    } catch (llmError) {
      console.warn("[IntentRouter] AI fallback unavailable, adhering to deterministic result:", llmError.message);
    }
  }

  return deterministicResult;
};

const IntentRouter = {
  normalizeCommand,
  stripWakeWords,
  hasWakeWord,
  extractCommandFromWakeWord,
  checkSensitiveCommand,
  checkLongAgriculturalRequest,
  parseAtomicCommand,
  matchDeterministicIntent,
  routeVoiceCommand
};

export default IntentRouter;
